import React, { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { PhoneCall, CheckCircle, ArrowRight } from 'lucide-react'

const Telecallers = () => {
  useEffect(() => {
    document.title = 'Telecallers - Shrijaa Outsourcing Consultants | Outbound & Inbound Calling Staff'
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Professional telecallers for sales, support, and lead generation. Shrijaa Outsourcing Consultants provides trained calling staff for BPOs, offices, and campaigns. Contact us today!'
      )
    }
  }, [])

  return (
    <div>
      <section className="bg-gradient-to-br from-primary-800 via-primary-700 to-teal-700 text-white py-16 md:py-20">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Telecallers</h1>
            <p className="text-lg md:text-xl text-primary-100">
              Trained telecalling staff for outbound sales, inbound support, and customer engagement
            </p>
            <div className="w-20 h-1 bg-accent-500 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-6">
                  <PhoneCall className="w-12 h-12 text-primary-600 mr-4" />
                  <h2 className="text-3xl md:text-4xl font-bold text-secondary-900">Expert Telecalling</h2>
                </div>
                <p className="text-secondary-600 leading-relaxed mb-6">
                  Shrijaa Outsourcing Consultants supplies professional telecallers for businesses that need reliable
                  calling capacity—whether for lead generation, follow-ups, appointment setting, or customer support.
                </p>
                <p className="text-secondary-600 leading-relaxed mb-6">
                  We focus on clear communication, professional tone, and consistency so your campaigns and daily calling
                  operations run smoothly.
                </p>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-secondary-50 rounded-lg p-8"
            >
              <img
                src="/service-telecallers.avif"
                alt="Professional telecaller with headset on a customer support call"
                className="w-full rounded-lg"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: <PhoneCall className="w-8 h-8" />,
                title: 'Trained Callers',
                description: 'Staff briefed on scripts, tone, and basic objection handling where applicable.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Flexible Deployment',
                description: 'Support for campaigns, shifts, and scaling headcount as your needs change.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Quality Focus',
                description: 'Emphasis on clarity, patience, and professional representation of your brand.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Outbound & inbound',
                description: 'Telecallers suited for sales outreach, reminders, and inbound query handling.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Office & BPO use',
                description: 'Staff for corporate floors, shared services, and calling-floor environments.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Verified profiles',
                description: 'Background-verified professionals you can onboard with confidence.'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="text-primary-600 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-primary-800 mb-3">{feature.title}</h3>
                <p className="text-secondary-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-primary-600 rounded-lg p-8 text-center text-white">
            <h3 className="text-3xl font-bold mb-4">Need Telecallers?</h3>
            <p className="text-xl text-primary-100 mb-6">Contact us today to discuss your calling and staffing requirements</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-primary-600 hover:bg-primary-50 font-semibold py-3 px-8 rounded-lg transition-colors inline-flex items-center justify-center"
              >
                Contact Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/services"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-colors"
              >
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Telecallers
